'use client';

// Reads the active proposal-form store and surfaces job-backed generate-flow
// progress via <AiProgressChip>. The 700ms "ready" linger after `done` lands
// so the resolution feels deliberate instead of the chip vanishing mid-animation.
import { c as _c } from "react/compiler-runtime";
import { detectPredictionStyle } from '@morfi/backend-sdk-v2/client';
import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { deriveAiProgressPhase } from '@/lib/ai/proposal-progress-controller';
import { useProposalFormStore } from '@/stores/proposal-form';
import { type AiPhase, AiProgressChip, type AiProgressMode } from './ai-progress-chip';
const FINISHING_LINGER_MS = 700;
export function AiStreamStatusChip() {
  const $ = _c(15);
  const state = useProposalFormStore();
  const status = useProposalFormStore(_temp);
  const aiError = useProposalFormStore(_temp2);
  const imagesExpected = useProposalFormStore(_temp3);
  const imagesSettled = useProposalFormStore(_temp4);
  let t0;
  if ($[0] !== state) {
    t0 = deriveAiProgressPhase(state);
    $[0] = state;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const phase = t0;
  const [finishing, setFinishing] = useState(false);
  const prevStatusRef = useRef(status);
  let t1;
  let t2;
  if ($[2] !== status) {
    t1 = () => {
      const prev = prevStatusRef.current;
      prevStatusRef.current = status;
      if (status === "done" && prev === "streaming") {
        setFinishing(true);
        const timer = setTimeout(() => setFinishing(false), FINISHING_LINGER_MS);
        return () => clearTimeout(timer);
      }
    };
    t2 = [status];
    $[2] = status;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  const mode = phase === "readyToCreate" ? "done" : phase === "finalizationFailed" || status === "error" ? "error" : phase !== "idle" && (status === "streaming" || phase === "cancelling") ? "progress" : finishing && status === "done" ? "done" : "hidden";
  if (mode === "done" && detectPredictionStyle(state.title)) {
    return null;
  }
  const displayPhase = phase === "idle" ? undefined : phase;
  let t3;
  if ($[5] !== displayPhase) {
    t3 = displayPhase === undefined ? {} : {
      phase: displayPhase
    };
    $[5] = displayPhase;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const phaseProps = t3;
  let t4;
  if ($[7] !== aiError) {
    t4 = aiError?.code === undefined ? {} : {
      errorCode: aiError.code
    };
    $[7] = aiError;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const errorCodeProps = t4;
  let t5;
  if ($[9] !== errorCodeProps || $[10] !== imagesExpected || $[11] !== imagesSettled || $[12] !== mode || $[13] !== phaseProps) {
    t5 = <AiProgressChip mode={mode} {...phaseProps} {...errorCodeProps} imagesExpected={imagesExpected} imagesSettled={imagesSettled} />;
    $[9] = errorCodeProps;
    $[10] = imagesExpected;
    $[11] = imagesSettled;
    $[12] = mode;
    $[13] = phaseProps;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}
function _temp4(s_2) {
  return s_2.aiImagesSettled;
}
function _temp3(s_1) {
  return s_1.aiImagesExpected;
}
function _temp2(s_0) {
  return s_0.aiError;
}
function _temp(s) {
  return s.aiStreamStatus;
}